import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getApiBaseUrl } from './apiBaseUrl';

const HEARTBEAT_INTERVAL_MS = 120000;
const EVENT_DEDUP_WINDOW_MS = 5000;
// A GPS fix older than this is treated as stale and left out of the heartbeat rather than sent as
// if it were current — a fix from 20 minutes ago no longer reliably says "wherever the app is
// running right now" if the device has moved since.
const GPS_FIX_MAX_AGE_MS = 5 * 60 * 1000;

let lastHeartbeatSignature = '';
let lastHeartbeatSentAt = 0;
// Refreshed independently of heartbeat sends (see refreshGpsFix) so a slow/denied geolocation call
// never delays a heartbeat — especially the pagehide/beacon one, which can't wait on anything.
let lastGpsFix = null; // { latitude, longitude, accuracyM, capturedAt }

const getKioskId = () => {
    const stored = String(localStorage.getItem('kiosk_id') || '').trim();
    const envValue = String(process.env.REACT_APP_KIOSK_ID || '').trim();
    return stored || envValue || '';
};

const getStoredValue = (key) => String(localStorage.getItem(key) || '').trim();

const refreshGpsFix = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
        (pos) => {
            lastGpsFix = {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                accuracyM: pos.coords.accuracy,
                capturedAt: Date.now()
            };
        },
        () => {
            // Permission denied / unavailable / timed out — heartbeat still sends without
            // location, same as a kiosk with no GPS hardware at all.
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
};

const buildHeartbeatPayload = (reason, pathname) => {
    const coordinates = getStoredValue('kiosk_coordinates');
    const city = getStoredValue('kiosk_city');
    const state = getStoredValue('kiosk_state');
    const landmark = getStoredValue('kiosk_landmark');
    const freshGpsFix = lastGpsFix && (Date.now() - lastGpsFix.capturedAt) <= GPS_FIX_MAX_AGE_MS
        ? lastGpsFix
        : null;

    return {
        kioskId: getKioskId(),
        reason,
        path: pathname,
        sentAt: new Date().toISOString(),
        source: 'kiosk-main-ui',
        appVersion: process.env.REACT_APP_KIOSK_APP_VERSION || 'browser-test',
        deviceStatus: navigator.onLine ? 'RUNNING' : 'OFFLINE',
        ...(freshGpsFix ? {
            latitude: freshGpsFix.latitude,
            longitude: freshGpsFix.longitude,
            accuracyM: freshGpsFix.accuracyM
        } : {}),
        payload: {
            source: 'kiosk-main-ui',
            route: pathname,
            reason,
            coordinates: coordinates || null,
            city: city || null,
            state: state || null,
            landmark: landmark || null,
            visibilityState: document.visibilityState,
            online: navigator.onLine
        }
    };
};

const canSendHeartbeat = (signature, force) => {
    if (force) return true;
    const now = Date.now();
    if (signature === lastHeartbeatSignature && now - lastHeartbeatSentAt < EVENT_DEDUP_WINDOW_MS) {
        return false;
    }
    lastHeartbeatSignature = signature;
    lastHeartbeatSentAt = now;
    return true;
};

const postHeartbeat = (reason, pathname, { useBeacon = false, force = false } = {}) => {
    const payload = buildHeartbeatPayload(reason, pathname);
    if (!payload.kioskId) return;

    const signature = `${reason}:${pathname}`;
    if (!canSendHeartbeat(signature, force)) return;

    const baseUrl = getApiBaseUrl();
    const endpoint = `${baseUrl}/api/kiosk/heartbeat`;
    const body = JSON.stringify(payload);

    if (useBeacon && navigator.sendBeacon) {
        const accepted = navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
        if (accepted) return;
    }

    fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true
    }).catch(() => {
        // Heartbeat failures should not interrupt the kiosk flow.
    });
};

const KioskHeartbeat = () => {
    const location = useLocation();

    useEffect(() => {
        refreshGpsFix();
        postHeartbeat('app-load', window.location.pathname, { force: true });

        const intervalId = window.setInterval(() => {
            refreshGpsFix();
            postHeartbeat('interval', window.location.pathname);
        }, HEARTBEAT_INTERVAL_MS);

        const handleFocus = () => postHeartbeat('focus', window.location.pathname);
        const handleOnline = () => {
            refreshGpsFix();
            postHeartbeat('online', window.location.pathname, { force: true });
        };
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                postHeartbeat('visibility', window.location.pathname);
            }
        };
        const handlePageHide = () => {
            postHeartbeat('pagehide', window.location.pathname, { useBeacon: true, force: true });
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('online', handleOnline);
        window.addEventListener('pagehide', handlePageHide);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('pagehide', handlePageHide);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        postHeartbeat('route-change', location.pathname, { force: true });
    }, [location.pathname]);

    return null;
};

export default KioskHeartbeat;
